import React, { useState } from 'react';
import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { useSaveHeader } from './SaveHeaderContext';
import { ChangeConfirmDialog } from './ChangeConfirmDialog';

const SAVE_HEADER_EVENT = 'Save Header Action';

export enum SaveHeaderAction {
  BUTTON_CLICKED = 'button_clicked',
  CONFIRM_CANCELLED = 'confirm_cancelled',
  CONFIRM_SAVED = 'confirm_saved',
  SAVE_SUCCEEDED = 'save_succeeded',
  SAVE_FAILED = 'save_failed',
}

export const HeaderSaveButton = () => {
  const { t } = useTranslation();
  const { activeHandle } = useSaveHeader();
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const posthog = usePostHog();

  if (!activeHandle) return null;

  const count = activeHandle.changes.length;
  const disabled = !activeHandle.dirty || !activeHandle.isValid || activeHandle.isSubmitting;
  const changedFields = activeHandle.changes.map((c) => c.label);

  const track = (action: SaveHeaderAction, extra?: Record<string, unknown>) => {
    posthog.capture(SAVE_HEADER_EVENT, {
      action,
      tab: activeHandle.tabKey,
      changes_count: count,
      changed_fields: changedFields,
      ...extra,
    });
  };

  const handleOpen = () => {
    track(SaveHeaderAction.BUTTON_CLICKED);
    setOpen(true);
  };

  const handleClose = async (confirmed: boolean) => {
    if (!confirmed) {
      track(SaveHeaderAction.CONFIRM_CANCELLED);
      setOpen(false);
      return;
    }
    track(SaveHeaderAction.CONFIRM_SAVED);
    try {
      await activeHandle.submit();
      track(SaveHeaderAction.SAVE_SUCCEEDED);
      setOpen(false);
    } catch (e) {
      track(SaveHeaderAction.SAVE_FAILED, { error: (e as Error)?.message });
      setOpen(false);
    }
  };

  return (
    <>
      <Stack
        direction={isSmallScreen ? 'column-reverse' : 'row'}
        alignItems={isSmallScreen ? 'flex-end' : 'center'}
        gap={isSmallScreen ? 0.5 : 1.5}>
        <Typography variant="body2" color="text.secondary">
          {activeHandle.dirty ? t('saveHeader.unsavedCount', { count }) : t('saveHeader.noChanges')}
        </Typography>
        <LoadingButton
          size="medium"
          variant="contained"
          color="primary"
          loading={activeHandle.isSubmitting}
          disabled={disabled}
          onClick={handleOpen}>
          {t('editOrderPage.orderPageBufferSubmit')}
        </LoadingButton>
      </Stack>
      <ChangeConfirmDialog open={open} onClose={handleClose} changes={activeHandle.changes} />
    </>
  );
};
